import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import WamoLogo from '@/website/assets/Whitelabel/wamo-logo.svg';
import BeazleyLogo from '@/website/assets/beazley-logo.png';
import PurpleSwoosh1 from '@/website/assets/business-page/Purple_corner_swish1_@2x.png';
import PurpleSwoosh2 from '@/website/assets/business-page/Purple_corner_swish2_@2x.png';
import PurpleSwoosh3 from '@/website/assets/business-page/Purple_corner_swish3_@2x.png';
import MaskGroupSwitcher from '@/website/assets/business-page/switcher-mask-group.png';
import ChubbLogo from '@/website/assets/chubb-logo.png';
import DeelLogo from '@/website/assets/deel-logo.svg';
import GlobeSrc from '@/website/assets/globe_with_swooshes.png';
import HiscoxLogo from '@/website/assets/logo-hiscox.svg';
import PockitLogo from '@/website/assets/pockit-logo.svg';
import BullhornLogo from '@/website/assets/recruiter-page/bullhorn-logo.png';
import StudiospaceLogo from '@/website/assets/studiospace-logo.svg';
import Footer from '@/website/components/Footer';
import Header from '@/website/components/Header';
import { BespokeInsurance } from '@/website/components/Landing/BusinessInsurance/BespokeInsurance';
import { BusinessFeatures } from '@/website/components/Landing/BusinessInsurance/BusinessFeatures';
import { InsuranceTypes } from '@/website/components/Landing/BusinessInsurance/InsuranceTypes';
import { OtherProducts } from '@/website/components/Landing/BusinessInsurance/OtherProducts';
import { useHubspotModal } from '@/website/components/Modals/HubspotModal';
import PromotionFloatingButton from '@/website/components/Promotions/PromotionFloatingButton';
import PromotionHero from '@/website/components/Promotions/PromotionHero';
import PromotionTopBanner from '@/website/components/Promotions/PromotionTopBanner';
import WidgetEmbed from '@/website/components/WidgetEmbed';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { useGlobalStore } from '@/website/store/GlobalStore';
import { useUploadFileExtractQuoteStore } from '@/website/store/uploadFileExtractQuoteStore';
import { IFeatureProps } from '@/website/types';
import { Routes } from '@/website/util/routes';
import { Button, PromotionGetResponse } from '@jove-frontend/uikit';
import { motion } from 'framer-motion';
import { lintrk } from 'nextjs-linkedin-insight-tag';
import { useAnalytics } from 'use-analytics';

const LinkedinConversionId = process.env.NX_LINKEDIN_CONVERSION_ID;

export const features = [
  {
    title: 'Professional indemnity',
    description:
      'This provides you financial protection from alleged negligence or failure to deliver a service as promised.',
    icon: (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M30.5001 11.1663C26.7031 11.1663 23.6251 14.2444 23.6251 18.0413C23.6251 21.8383 26.7031 24.9163 30.5001 24.9163C34.297 24.9163 37.3751 21.8383 37.3751 18.0413C37.3751 14.2444 34.297 11.1663 30.5001 11.1663ZM19.0417 18.0413C19.0417 11.7131 24.1718 6.58301 30.5001 6.58301C36.8283 6.58301 41.9584 11.7131 41.9584 18.0413C41.9584 24.3696 36.8283 29.4997 30.5001 29.4997C24.1718 29.4997 19.0417 24.3696 19.0417 18.0413ZM16.9402 47.833H44.06C42.969 41.3299 37.3132 36.3747 30.5001 36.3747C23.687 36.3747 18.0312 41.3299 16.9402 47.833ZM12.1667 50.1247C12.1667 39.9995 20.3749 31.7913 30.5001 31.7913C40.6253 31.7913 48.8334 39.9995 48.8334 50.1247C48.8334 51.3903 47.8074 52.4163 46.5417 52.4163H14.4584C13.1928 52.4163 12.1667 51.3903 12.1667 50.1247Z"
          fill="#333333"
        />
        <path
          d="M55.7631 14.3278C55.7631 21.0052 50.35 26.4183 43.6725 26.4183C36.9951 26.4183 31.582 21.0052 31.582 14.3278C31.582 7.65041 36.9951 2.2373 43.6725 2.2373C50.35 2.2373 55.7631 7.65041 55.7631 14.3278Z"
          fill="#75BCB4"
        />
        <path
          d="M44.4722 12.7275V7.12695L37.2715 15.9279H42.8721L42.8721 21.5284L50.0728 12.7275L44.4722 12.7275Z"
          fill="#333333"
        />
      </svg>
    ),
  },
  {
    title: 'Public liability',
    description:
      'Protects you from any injury to clients or third parties or damage to their property caused by you, or your alleged negligence.',
    icon: (
      <svg
        width="60"
        height="60"
        viewBox="0 0 60 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M30.5001 12.1663C26.7031 12.1663 23.6251 15.2444 23.6251 19.0413C23.6251 22.8383 26.7031 25.9163 30.5001 25.9163C34.297 25.9163 37.3751 22.8383 37.3751 19.0413C37.3751 15.2444 34.297 12.1663 30.5001 12.1663ZM19.0417 19.0413C19.0417 12.7131 24.1718 7.58301 30.5001 7.58301C36.8283 7.58301 41.9584 12.7131 41.9584 19.0413C41.9584 25.3696 36.8283 30.4997 30.5001 30.4997C24.1718 30.4997 19.0417 25.3696 19.0417 19.0413ZM16.9402 48.833H44.06C42.969 42.3299 37.3132 37.3747 30.5001 37.3747C23.687 37.3747 18.0312 42.3299 16.9402 48.833ZM12.1667 51.1247C12.1667 40.9995 20.3749 32.7913 30.5001 32.7913C40.6253 32.7913 48.8334 40.9995 48.8334 51.1247C48.8334 52.3903 47.8074 53.4163 46.5417 53.4163H14.4584C13.1928 53.4163 12.1667 52.3903 12.1667 51.1247Z"
          fill="#333333"
        />
        <ellipse
          cx="45.6723"
          cy="15.3276"
          rx="12.3276"
          ry="12.3276"
          fill="#75BCB4"
        />
        <path
          d="M40.2089 11.7499C38.959 12.9997 38.959 15.0261 40.2089 16.276L45.6724 21.7395L51.1358 16.276C52.3857 15.0261 52.3857 12.9997 51.1358 11.7499C49.886 10.5 47.8596 10.5 46.6097 11.7499L45.6724 12.6873L44.735 11.7499C43.4851 10.5 41.4587 10.5 40.2089 11.7499Z"
          fill="#333333"
        />
      </svg>
    ),
  },
  {
    title: 'Employers liability',
    description:
      'Covers you when you have staff and they are injured in the course of business for which you are alleged negligent.',
    icon: (
      <svg
        width="55"
        height="55"
        viewBox="0 0 55 55"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M43.5417 48.125V11.4583C43.5417 8.92703 41.4896 6.875 38.9583 6.875H16.0417C13.5104 6.875 11.4583 8.92703 11.4583 11.4583V48.125M43.5417 48.125L48.125 48.1249M43.5417 48.125H32.0833M11.4583 48.125L6.875 48.1249M11.4583 48.125H22.9167M20.625 16.0416H22.9167M20.625 25.2083H22.9167M32.0833 16.0416H34.375M32.0833 25.2083H34.375M22.9167 48.125V36.6666C22.9167 35.401 23.9427 34.375 25.2083 34.375H29.7917C31.0573 34.375 32.0833 35.401 32.0833 36.6666V48.125M22.9167 48.125H32.0833"
          stroke="#333333"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="42.6723" cy="12.3276" r="12.3276" fill="#75BCB4" />
        <path
          d="M37.4459 7.80946C36.1961 9.0593 36.1961 11.0857 37.4459 12.3355L42.9094 17.7991L48.3729 12.3355C49.6227 11.0857 49.6227 9.0593 48.3729 7.80945C47.123 6.55961 45.0966 6.55961 43.8468 7.80946L42.9094 8.74688L41.972 7.80946C40.7222 6.55961 38.6958 6.55961 37.4459 7.80946Z"
          fill="#333333"
        />
      </svg>
    ),
  },
  {
    title: 'Directors and office liability',
    description:
      'Referred to as D&O, covers individuals who are in senior leadership or board members who could be personally pursued for their actions whilst serving the business.',
    icon: (
      <svg
        width="61"
        height="60"
        viewBox="0 0 61 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="28.625"
          y="8.96875"
          width="21.875"
          height="15.5"
          fill="#75BCB4"
        />
        <path
          d="M10.5 51.75V31.625C10.5 30.5204 11.3954 29.625 12.5 29.625H48.5C49.6046 29.625 50.5 30.5204 50.5 31.625V51.75"
          stroke="#333333"
          strokeWidth="4"
        />
        <path d="M28.625 37L28.625 51.75" stroke="#333333" strokeWidth="4" />
        <path d="M37.6309 22.75V28.125" stroke="#333333" strokeWidth="4" />
        <path d="M10.5 37H50.5" stroke="#333333" strokeWidth="4" />
        <path d="M10.5 45H27.375" stroke="#333333" strokeWidth="4" />
        <mask id="path-7-inside-1_6702_59004" fill="white">
          <rect
            x="24.7617"
            y="6.0293"
            width="25.7383"
            height="19.4081"
            rx="3"
          />
        </mask>
        <rect
          x="24.7617"
          y="6.0293"
          width="25.7383"
          height="19.4081"
          rx="3"
          stroke="#333333"
          strokeWidth="8"
          mask="url(#path-7-inside-1_6702_59004)"
        />
      </svg>
    ),
  },
  {
    title: 'Personal accident insurance ',
    description:
      'Will pay a benefit if someone working for the business suffers an accident resulting in loss of sight, hearing, speech or a limb, death or leaving them disabled and permanently unable to work.',
    icon: (
      <svg
        width="61"
        height="60"
        viewBox="0 0 61 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30.8813 47.6358V19.069L39.7714 13.8535L47.1206 15.6315L51.7434 21.9139C51.5854 23.6919 51.2219 27.3902 51.0322 27.9591C50.8426 28.5281 37.5193 41.314 30.8813 47.6358Z"
          fill="#75BCB4"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M35.6409 18.0995C35.6409 18.0995 35.6409 18.0996 35.6408 18.0996L32.6206 21.1201C32.1908 21.5499 31.6079 21.7914 31.0001 21.7914C30.3923 21.7914 29.8094 21.5499 29.3796 21.1202L26.359 18.0996C23.2267 14.9672 18.1482 14.9672 15.0158 18.0996C11.8835 21.2319 11.8835 26.3104 15.0158 29.4427L31 45.4269L46.984 29.4428L48.6045 31.0632L46.984 29.4427C50.1163 26.3104 50.1163 21.2319 46.984 18.0996C43.8517 14.9673 38.7732 14.9672 35.6409 18.0995ZM32.3999 14.8587C37.3222 9.93643 45.3027 9.93643 50.2249 14.8587C55.1471 19.7809 55.1471 27.7614 50.2249 32.6836L32.6205 50.2883C32.1907 50.7181 31.6078 50.9595 31 50.9595C30.3923 50.9595 29.8094 50.7181 29.3796 50.2883L11.7749 32.6836C6.85269 27.7614 6.85269 19.7809 11.7749 14.8587C16.6972 9.93643 24.6777 9.93643 29.5999 14.8587L31 16.2587L32.3999 14.8587L32.3999 14.8587Z"
          fill="#333333"
        />
      </svg>
    ),
  },
  {
    title: 'Cyber insurance',
    description:
      'Will help protect you in the event of a malicious attack on your systems or data. Also assists in minimising disruption to your business and covers financial costs involved in recovering from a cyber attack.',
    icon: (
      <svg
        width="61"
        height="60"
        viewBox="0 0 61 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="24.75" y="24.25" width="11.75" height="11.25" fill="#75BCB4" />
        <path
          d="M24.5 12V16M36.5 12V16M24.5 44V48M36.5 44V48M16.5 24H12.5M16.5 36H12.5M48.5 24H44.5M48.5 36H44.5M20.5 44H40.5C42.7091 44 44.5 42.2091 44.5 40V20C44.5 17.7909 42.7091 16 40.5 16H20.5C18.2909 16 16.5 17.7909 16.5 20V40C16.5 42.2091 18.2909 44 20.5 44ZM24.5 24H36.5V36H24.5V24Z"
          stroke="#333333"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: 'Portable equipment insurance',
    description:
      'Covers portable items that are used for your trade or profession, this could be a laptop, phone, camera, scientific instrument or measuring device.',
    icon: (
      <svg
        width="56"
        height="55"
        viewBox="0 0 56 55"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M48.5267 23.3219L6.92529 12.7371L8.37635 9.05147L10.0694 6.63281H44.1731L48.5267 9.05147V23.3219Z"
          fill="#75BCB4"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.08325 11.458C5.08325 7.66105 8.16129 4.58301 11.9583 4.58301H44.0416C47.8385 4.58301 50.9166 7.66105 50.9166 11.458V34.3747C50.9166 38.1716 47.8385 41.2497 44.0416 41.2497H36.0913L36.9442 44.6614L38.787 46.5042C39.4425 47.1596 39.6385 48.1453 39.2838 49.0017C38.9291 49.858 38.0935 50.4163 37.1666 50.4163H18.8333C17.9064 50.4163 17.0707 49.858 16.716 49.0017C16.3613 48.1453 16.5574 47.1596 17.2128 46.5042L19.0556 44.6614L19.9086 41.2497H11.9583C8.16129 41.2497 5.08325 38.1716 5.08325 34.3747V11.458ZM9.66659 32.083V34.3747C9.66659 35.6403 10.6926 36.6663 11.9583 36.6663H22.7923C22.8253 36.6656 22.8584 36.6656 22.8916 36.6663H33.1082C33.1414 36.6656 33.1746 36.6656 33.2076 36.6663H44.0416C45.3072 36.6663 46.3333 35.6403 46.3333 34.3747V32.083H9.66659ZM46.3333 27.4997H9.66659V11.458C9.66659 10.1924 10.6926 9.16634 11.9583 9.16634H44.0416C45.3072 9.16634 46.3333 10.1924 46.3333 11.458V27.4997ZM31.3669 41.2497H24.6329L23.4871 45.833H32.5127L31.3669 41.2497Z"
          fill="#333333"
        />
      </svg>
    ),
  },
] as IFeatureProps[];

export const instantPricingList = [
  {
    title: 'Get within 90 seconds',
    description: 'Be able to obtain and purchase cover within 90 seconds',
    icon: (
      <svg
        width="30"
        height="31"
        viewBox="0 0 30 31"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          opacity="0.8"
          cx="12.2722"
          cy="18.8376"
          r="10.2146"
          fill="#75BCB4"
        />
        <path
          d="M14.1683 6.09972V24.467C14.1683 25.8034 13.0521 26.8867 11.6752 26.8867C10.6216 26.8867 9.68175 26.2439 9.32738 25.2808L6.28663 16.8257M24.085 15.8867C26.4322 15.8867 28.335 14.0399 28.335 11.7617C28.335 9.48354 26.4322 7.63672 24.085 7.63672M6.28663 16.8257C4.25772 15.9886 2.83496 14.0364 2.83496 11.7617C2.83496 8.72415 5.37201 6.26172 8.50162 6.26172H11.0971C16.9063 6.26172 21.8989 4.56484 24.085 2.13672L24.085 21.3867C21.8989 18.9586 16.9063 17.2617 11.0971 17.2617L8.50159 17.2617C7.71563 17.2617 6.96705 17.1064 6.28663 16.8257Z"
          stroke="#333333"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: 'Flexible Cover',
    description: 'Choose the cover you need at the level you want',
    icon: (
      <svg
        width="32"
        height="33"
        viewBox="0 0 32 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle opacity="0.8" cx="11" cy="21.5" r="10" fill="#75BCB4" />
        <path
          d="M16 15.5C15.4477 15.5 15 15.9477 15 16.5C15 17.0523 15.4477 17.5 16 17.5V15.5ZM20 17.5C20.5522 17.5 21 17.0523 21 16.5C21 15.9477 20.5522 15.5 20 15.5V17.5ZM16 20.8333C15.4477 20.8333 15 21.281 15 21.8333C15 22.3856 15.4477 22.8333 16 22.8333V20.8333ZM20 22.8333C20.5522 22.8333 21 22.3856 21 21.8333C21 21.281 20.5522 20.8333 20 20.8333V22.8333ZM12 15.5C11.4477 15.5 11 15.9477 11 16.5C11 17.0523 11.4477 17.5 12 17.5V15.5ZM12.0133 17.5C12.5656 17.5 13.0133 17.0523 13.0133 16.5C13.0133 15.9477 12.5656 15.5 12.0133 15.5V17.5ZM12 20.8333C11.4477 20.8333 11 21.281 11 21.8333C11 22.3856 11.4477 22.8333 12 22.8333V20.8333ZM12.0133 22.8333C12.5656 22.8333 13.0133 22.3856 13.0133 21.8333C13.0133 21.281 12.5656 20.8333 12.0133 20.8333V22.8333ZM24.3333 9.83333V25.8333H26.3333V9.83333H24.3333ZM22.6666 27.5H9.33329V29.5H22.6666V27.5ZM7.66663 25.8333V9.83333H5.66663V25.8333H7.66663ZM9.33329 8.16667H12V6.16667H9.33329V8.16667ZM20 8.16667H22.6666V6.16667H20V8.16667ZM9.33329 27.5C8.41282 27.5 7.66663 26.7538 7.66663 25.8333H5.66663C5.66663 27.8584 7.30825 29.5 9.33329 29.5V27.5ZM24.3333 25.8333C24.3333 26.7538 23.5871 27.5 22.6666 27.5V29.5C24.6917 29.5 26.3333 27.8584 26.3333 25.8333H24.3333ZM26.3333 9.83333C26.3333 7.80829 24.6917 6.16667 22.6666 6.16667V8.16667C23.5871 8.16667 24.3333 8.91286 24.3333 9.83333H26.3333ZM7.66663 9.83333C7.66663 8.91286 8.41282 8.16667 9.33329 8.16667V6.16667C7.30825 6.16667 5.66663 7.80829 5.66663 9.83333H7.66663ZM16 17.5H20V15.5H16V17.5ZM16 22.8333H20V20.8333H16V22.8333ZM14.6666 5.5H17.3333V3.5H14.6666V5.5ZM17.3333 8.83333H14.6666V10.8333H17.3333V8.83333ZM14.6666 8.83333C13.7462 8.83333 13 8.08714 13 7.16667H11C11 9.19171 12.6416 10.8333 14.6666 10.8333V8.83333ZM19 7.16667C19 8.08714 18.2538 8.83333 17.3333 8.83333V10.8333C19.3583 10.8333 21 9.19171 21 7.16667H19ZM17.3333 5.5C18.2538 5.5 19 6.24619 19 7.16667H21C21 5.14162 19.3583 3.5 17.3333 3.5V5.5ZM14.6666 3.5C12.6416 3.5 11 5.14162 11 7.16667H13C13 6.24619 13.7462 5.5 14.6666 5.5V3.5ZM12 17.5H12.0133V15.5H12V17.5ZM12 22.8333H12.0133V20.8333H12V22.8333Z"
          fill="#333333"
        />
      </svg>
    ),
  },
  {
    title: 'No hidden fees or charges',
    description:
      'Transparent pricing allows you to see how much each cover is costing. No additional fees or charges are added,',
    icon: (
      <svg
        width="33"
        height="32"
        viewBox="0 0 33 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle opacity="0.8" cx="22" cy="11.5" r="10" fill="#75BCB4" />
        <path
          d="M5.8783 9.33333V22.6667C5.8783 25.6122 10.6539 28 16.545 28C22.436 28 27.2116 25.6122 27.2116 22.6667V9.33333M5.8783 9.33333C5.8783 12.2789 10.6539 14.6667 16.545 14.6667C22.436 14.6667 27.2116 12.2789 27.2116 9.33333M5.8783 9.33333C5.8783 6.38781 10.6539 4 16.545 4C22.436 4 27.2116 6.38781 27.2116 9.33333M27.2116 16C27.2116 18.9455 22.436 21.3333 16.545 21.3333C10.6539 21.3333 5.8783 18.9455 5.8783 16"
          stroke="#333333"
          strokeWidth="2"
        />
      </svg>
    ),
  },
] as IFeatureProps[];

export default function BusinessInsurancePage({
  promotion,
}: {
  promotion?: PromotionGetResponse;
}) {
  const router = useRouter();
  const { track } = useAnalytics();
  const { openQuote, openRenew } = router.query;
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState<boolean>(false);
  const {
    actions: { cleanImportExtractQuoteRelatedInfo },
  } = useUploadFileExtractQuoteStore();
  const {
    actions: { updateCompanySlug },
  } = useGlobalStore();
  const { push } = useCustomNavigate();
  function handleCloseQuoteModal(e: MessageEvent<string>) {
    if (e.data === 'close-iframe') {
      setIsQuoteModalOpen(false);
    }
  }

  useEffect(() => {
    updateCompanySlug((router.query?.['company_slug'] as string) || '');

    if (typeof window !== 'undefined') {
      window.addEventListener('message', handleCloseQuoteModal);
    }

    return () => {
      window.removeEventListener('message', handleCloseQuoteModal);
    };
  }, []);

  useEffect(() => {
    if (isQuoteModalOpen) {
      track('open_quote_modal');
      track('click_get_quote_button');
      window.lintrk && lintrk('track', LinkedinConversionId);
    }
  }, [isQuoteModalOpen]);

  const { HubspotModal, setShowHubspotModal } = useHubspotModal({
    props: {
      formId: 'da63a1b7-3916-4288-a244-120de7cb8d4b',
      title: 'Switch to Jove at renewal',
      description: (
        <>
          Send us your current insurance policy and let us try to beat your
          quote at renewal, subject to{' '}
          <Link
            href="/legal/terms-and-conditions"
            target="_blank"
            className="text-brand-primary"
          >
            Jove's T&C.
          </Link>
        </>
      ),
    },
  });

  useEffect(() => {
    if (openQuote) {
      setIsQuoteModalOpen(true);
    } else if (openRenew) {
      setShowHubspotModal(true);
    }
  }, [openQuote, openRenew]);

  const switchToJoveAction = () => {
    cleanImportExtractQuoteRelatedInfo();
    push({ path: Routes.switchToJoveHome() });
  };

  /** Promotion logic */
  const hasActivePromotion = promotion?.status === 'active';
  const insuranceType = new Map([
    ['goods-in-transit-insurance', 'goods-in-transit'],
    ['business-insurance', 'business-insurance'],
    ['construction-insurance', 'hired-in-plant'],
  ]);
  const queryParams = new URLSearchParams();
  if (hasActivePromotion) {
    queryParams.set('utm_source', 'jove-website');
    queryParams.set('utm_medium', 'promotion');
    queryParams.set('utm_campaign', promotion.slug);
    queryParams.set('promotion_id', promotion.id);
    queryParams.set(
      'display_insurance',
      insuranceType.get(promotion.insurance_type)
    );
    queryParams.set('providerCode', promotion.insurance_providers[0].code);
  }

  const quoteSrc = hasActivePromotion
    ? `${process.env.NX_QUOTE_ENDPOINT}/?${queryParams.toString()}`
    : `${process.env.NX_QUOTE_ENDPOINT}`;
  /** End of promotion logic */

  return (
    <div className="prose mx-auto w-full">
      {hasActivePromotion && (
        <>
          <PromotionTopBanner
            endDate={promotion.end_date}
            text={promotion.banner_settings.top_banner_text}
          />
          <PromotionFloatingButton
            discount={Number(promotion.discount)}
            onClick={() => setIsQuoteModalOpen(true)}
          />
        </>
      )}

      <Header className="border-none shadow-none" />

      {hasActivePromotion ? (
        <PromotionHero
          promotion={promotion}
          onClick={() => setIsQuoteModalOpen(true)}
        />
      ) : (
        <div className="relative overflow-hidden bg-white py-20 lg:py-32">
          <div className="mx-auto w-full max-w-7xl px-2">
            <div className="bg-dark-blue relative z-10 mx-auto mt-72 max-w-[576px] rounded-3xl p-10 text-white shadow-2xl shadow-black/50 lg:m-0">
              <motion.h2
                className="mb-6 text-5xl text-white"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                Flexible. Borderless.{' '}
                <span className="font-medium">Insurance</span>
              </motion.h2>

              <motion.p
                className="mb-16 hidden text-xl font-medium text-white lg:block"
                initial={{ opacity: 0, scale: 0.2 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Insurance for gig workers, contractors and SMEs, that covers you
                globally allowing you to work remotely anywhere in the world,
                with the ability to pause and activate your cover in seconds.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Button
                  color="secondary-outline-new"
                  onClick={() => setIsQuoteModalOpen(true)}
                >
                  <span className="px-2 font-bold">Get a quote</span>
                </Button>
              </motion.div>
            </div>
          </div>

          <Image
            src={GlobeSrc}
            alt="globe"
            className="absolute -right-0 top-10 z-0 -mt-5 -rotate-6 lg:-top-1/4 lg:left-1/3"
          />
        </div>
      )}

      <div className="mx-auto mb-8 mt-5 flex max-w-7xl flex-wrap items-end justify-center space-x-5 px-2 sm:justify-between sm:space-x-3">
        <Image
          src={DeelLogo}
          alt="deel"
          width={62}
          height={22}
          style={{ objectFit: 'contain' }}
        />
        <Image
          src={StudiospaceLogo}
          alt="studiospace"
          width={127}
          height={22}
          style={{ objectFit: 'contain' }}
          className="-mb-1"
        />
        <Image
          src={PockitLogo}
          alt="pockit"
          width={91}
          height={22}
          style={{ objectFit: 'contain' }}
        />
        <Image
          src={WamoLogo}
          alt="wamo"
          width={130}
          height={22}
          style={{ objectFit: 'contain' }}
          className="mt-10 sm:mt-0"
        />
        <Image
          src={ChubbLogo}
          alt="chubb"
          width={118}
          height={22}
          style={{ objectFit: 'contain' }}
          className="-mb-3"
        />
        <Image
          src={HiscoxLogo}
          alt="hiscox"
          width={70}
          height={30}
          style={{ objectFit: 'contain' }}
          className="mt-4 sm:mt-0"
        />
        <Image
          src={BeazleyLogo}
          alt="beazley"
          width={118}
          height={22}
          style={{ objectFit: 'contain' }}
          className="-mb-9 sm:-mb-8"
        />
        <Image
          src={BullhornLogo}
          alt="bullhorn"
          width={118}
          height={22}
          style={{ objectFit: 'contain' }}
          className="-mb-2 mt-4 sm:mt-0"
        />
      </div>

      <div className="mx-auto my-16 w-full max-w-7xl px-2">
        <div className="relative flex w-full flex-col items-center justify-between overflow-hidden rounded-2xl bg-[#f4f4f4] p-6 shadow-xl shadow-black/10 lg:flex-row lg:px-[70px] lg:py-10">
          <div className="absolute left-0 top-0 z-0 m-0 h-1/2 lg:h-full">
            <Image src={MaskGroupSwitcher} alt="mask-group-switcher" />
          </div>

          <div className="flex justify-center py-14 lg:p-0">
            <Button
              color="secondary-outline-new"
              className="lg:ml-6"
              // onClick={() => {
              //   window.location.href =
              //     'https://qj8ibjpscc3.typeform.com/to/Ls4OuVWV?typeform-source=admin.typeform.com';
              // }}
              onClick={switchToJoveAction}
            >
              <span className="font-bold">Switch to Jove</span>
            </Button>
          </div>

          <div className="relative z-10 lg:w-2/3 lg:pl-10">
            <h3 className="text-[2rem] font-medium lg:text-[2.5rem]">
              Cut your costs with the{' '}
              <span className="font-black">Jove Switcher</span>
            </h3>

            <p className="text-xl">Let us try to beat your existing quote</p>
          </div>
        </div>
      </div>

      <div className="bg-black/5">
        <div className="mx-auto w-full max-w-7xl py-20">
          <h2 className="text-center">
            Designed <span className="font-medium">to suit your needs</span>
          </h2>

          <BusinessFeatures />

          <Button
            color="primary-new"
            onClick={() => setIsQuoteModalOpen(true)}
            className="mx-auto"
          >
            <span className="px-2 font-bold">Get a quote</span>
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-2 py-20">
        <h2 className="mb-32 text-center">
          <span className="font-medium">Our </span>instantly quotable{' '}
          <span className="font-medium">insurance types</span>
        </h2>

        <InsuranceTypes />

        <Button
          color="primary-new"
          onClick={() => setIsQuoteModalOpen(true)}
          className="mx-auto"
        >
          <span className="px-2 font-bold">Get a quote</span>
        </Button>
      </div>

      <div className="bg-black/5">
        <div className="mx-auto max-w-7xl px-2 py-20">
          <h2 className="text-center font-medium">Get an instant quote</h2>

          <div className="my-14 grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="bg-dark-blue relative flex flex-col justify-between overflow-hidden rounded-2xl p-8 text-white">
              <div className="relative z-10">
                <p className="text-2xl font-medium">Get a quote in</p>

                <h1 className="mb-8 text-white">
                  90 <br /> Seconds
                </h1>

                <p className="font-medium">
                  Quote and purchase cover in just a few clicks
                </p>
              </div>

              <Image
                src={PurpleSwoosh1}
                alt="swoosh"
                className="absolute left-0 top-0 z-0 m-0 h-full w-auto"
              />
            </div>

            <div className="bg-dark-blue relative flex flex-col justify-between overflow-hidden rounded-2xl p-8 text-white">
              <div className="relative z-10">
                <h1 className="mb-8 text-white">
                  Flexible <br /> cover
                </h1>

                <p className="font-medium">
                  Choose the cover you need at the level you want
                </p>
              </div>

              <Image
                src={PurpleSwoosh2}
                alt="swoosh"
                className="absolute right-0 top-0 z-0 m-0 h-full w-auto"
              />
            </div>

            <div className="bg-dark-blue relative flex flex-col justify-between overflow-hidden rounded-2xl p-8 text-white">
              <div className="relative z-10">
                <h1 className="mb-8 text-white">
                  No hidden <br /> fees
                </h1>

                <p className="font-medium">
                  Transparent pricing allows you to see how much each cover is
                  costing.
                </p>
              </div>

              <Image
                src={PurpleSwoosh3}
                alt="swoosh"
                className="absolute right-0 top-0 z-0 m-0 h-full w-auto"
              />
            </div>
          </div>

          <Button
            color="primary-new"
            onClick={() => setIsQuoteModalOpen(true)}
            className="mx-auto"
          >
            <span className="px-2 font-bold">Get a quote</span>
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-2 py-20">
        <h2 className="text-center">
          <span className="font-medium">Interested in </span>other products?
        </h2>

        <OtherProducts />

        <div className="flex flex-col items-center justify-center space-y-4 lg:flex-row lg:space-x-4 lg:space-y-0">
          <Button
            color="primary-new"
            onClick={() => {
              window.open(
                'https://calendly.com/d/ck5r-s7s-3fx/jove-broker-desk',
                '_blank',
                'noopener,noreferrer'
              );
            }}
          >
            <span className="font-bold">Contact our broker desk</span>
          </Button>
          <Button
            color="secondary-outline-new"
            onClick={() => setIsQuoteModalOpen(true)}
          >
            <span className="font-bold">Get a quote</span>
          </Button>
        </div>
      </div>

      <div className="mx-auto mb-24 max-w-7xl px-2">
        <BespokeInsurance />
      </div>

      <Footer />

      <HubspotModal />

      <WidgetEmbed isOpen={isQuoteModalOpen} quoteSrc={quoteSrc} />
    </div>
  );
}
